// src/encounter/lib/chief-complaint-hpi.ts
// PROD-2136 — the chief-complaint HPI BLOCK: the Flutter provider's dynamic per-complaint HPI,
// rendered in the HPI step where Template Studio authors a marker group.
//
// THE MARKER. Template Studio emits, inside the HPI section:
//   { linkId: 'hpi-chief-complaint', type: 'group',
//     extension: [{ url: …/questionnaire-itemControl,
//                   valueCodeableConcept: { coding: [{ code: 'chief-complaint-hpi' }] } }] }
// Detection reads `QItem.itemControl` first and the linkId second.
//
// IT USED TO READ A PA-ONLY FIELD, AND NO LONGER NEEDS TO (Q2, 2026-09-16). `chief-complaint-hpi`
// was outside the DS role vocabulary, so `fhirToDsQuestionnaire` lifted the raw code onto a
// PA-local `PaQItem.itemControlCode` to keep it visible. Samer's Q2 ruling made the block a real
// Signal DS component and the code a real DS role, so `asItemControlRole` now narrows it like any
// other and it arrives on `QItem.itemControl`. The PA-only carrier is gone.
//
// WHAT STILL DETECTS, AND WHY, now that the DS draws the block itself: the shell needs to know a
// marker exists so it can place the chief-complaint EDITOR beside it, keep the marker out of the
// chip rail's jump targets, and report `data-cc-placement`. The generated note needs to know so it
// can splice in the complaint categories. Neither is about rendering the block.
//
// A template without the marker changes NOTHING — the HPI step renders exactly as before.
//
// THE DATA is NOT the QuestionnaireResponse. It is Flutter's own chief-complaint document
// (the PROD-1588 seam, use-chief-complaint.ts / to-chief-complaint.ts): each complaint on
// `GET /api/Patients/GetPatientComplaint` carries `templateItem[]` — the attribute groups the
// SERVER resolved for the complaint's category (Onset · Character · …, each with
// `templateItemObservation[]` options) — and `patientHpi[]`, the saved answers. Selecting a chip
// appends a `patientHpi` entry; deselecting drops it; the FULL complaint list is re-posted through
// the existing `CreateChiefComplaint` replace-all (kept complaints echoed, `templateItem`
// stripped as today). No new endpoint.
//
// THE ENTRY SHAPE is Flutter's `PatientClinicalCategoryData.toTemplate()`
// (scrips_shared_features/lib/features/patient_module/patient_chart/data/datamodels/
// generated_notes_data.dart:525-576) serialised by `EncounterTemplateCategoryModel.toMap()`
// (common/data/datamodels/encounter/encounter_template_category_model.dart:128-152), and was
// confirmed against a captured dev POST from the Flutter provider app on 2026-09-11 — see
// `newPatientHpiEntry`.
import type {
  Questionnaire,
  QItem,
  ChiefComplaintHpiCategory,
  ChiefComplaintHpiComplaint,
} from '@scripsteam/scrips-signal-ds';
import type {
  PatientHpiEntry,
  TemplateItem,
  TemplateItemObservation,
} from '@/types/encounter-template-api';
import { isComplaintEntry, type ComplaintEntry, type PatientComplaintModel } from './to-chief-complaint';
import type { NoteSection } from '../generated-note';

// ── Marker detection ─────────────────────────────────────────────────────────────────────────

/** The itemControl coding CODE Template Studio stamps on the block's group. Matched on code
 *  only — the coding's `system` is not required (the Studio may or may not set one). */
export const CHIEF_COMPLAINT_HPI_ITEM_CONTROL = 'chief-complaint-hpi';
/** Secondary match: the group's authored linkId. */
export const CHIEF_COMPLAINT_HPI_LINK_ID = 'hpi-chief-complaint';

/** Is this item the Studio's chief-complaint HPI block marker? A GROUP carrying either the
 *  itemControl code or the reserved linkId. Leaf items never match (a `choice` item that happens
 *  to be named `hpi-chief-complaint` is an authored question, not the block). */
export function isChiefComplaintHpiMarker(item: QItem): boolean {
  if (item.type !== 'group') return false;
  return (
    item.itemControl === CHIEF_COMPLAINT_HPI_ITEM_CONTROL ||
    item.linkId === CHIEF_COMPLAINT_HPI_LINK_ID
  );
}

/** The first marker group anywhere in the tree (document order, depth-first), or `undefined`. */
export function findChiefComplaintHpiMarker(items: ReadonlyArray<QItem>): QItem | undefined {
  for (const it of items) {
    if (isChiefComplaintHpiMarker(it)) return it;
    if (it.item) {
      const nested = findChiefComplaintHpiMarker(it.item);
      if (nested) return nested;
    }
  }
  return undefined;
}

/**
 * The Studio's HPI layout sample: the category cards it seeds beside the marker — Location · Character · Onset
 * (dev-scrips-pm-react `src/studio/template-studio/model/presets/encounter-preset-v4.ts`, the HPI page: the marker,
 * then `hpi-location`, `hpi-character`, `hpi-onset`; no other preset seeds cards beside the marker).
 */
export const STUDIO_HPI_SAMPLE_LINK_IDS: ReadonlySet<string> = new Set(['hpi-location', 'hpi-character', 'hpi-onset']);

/**
 * A Studio sample category CARD: one of the seeded linkIds, and a group whose every child is a boolean chip. Matched
 * by linkId, never by shape alone (Scrips Agent review at 665cde84): an AUTHORED yes/no group beside the marker — a
 * red-flag checklist — is the template's content and still renders.
 */
function isCategoryCard(item: QItem): boolean {
  return (
    STUDIO_HPI_SAMPLE_LINK_IDS.has(item.linkId) &&
    item.type === 'group' &&
    !!item.item?.length &&
    item.item.every((c) => c.type === 'boolean')
  );
}

/**
 * The HPI section with the Studio's sample category cards removed from beside the marker
 * (Ahmed, 2026-09-28). The Studio seeds Location · Character · Onset next to the marker as a
 * LAYOUT SAMPLE and keeps drawing them on its canvas; in the PA a complaint's categories are the
 * server's, per complaint (`templateItem[]`, drawn inside the block), so the template's copies
 * would be default cards on an encounter that has no complaint yet. Only the marker's siblings,
 * only category-card groups — any other authored group (free text, a date) still renders, and a
 * template without the marker is returned unchanged.
 */
export function stripHpiSampleCategories(questionnaire: Questionnaire): Questionnaire {
  const prune = (list: QItem[]): QItem[] => {
    const hasMarker = list.some(isChiefComplaintHpiMarker);
    return list
      .filter((it) => !(hasMarker && isCategoryCard(it)))
      .map((it) => (it.item ? { ...it, item: prune(it.item) } : it));
  };
  return { ...questionnaire, item: prune(questionnaire.item) };
}

/** Does the questionnaire (or the HPI section of it) author the block? */
export function hasChiefComplaintHpiBlock(questionnaire: Questionnaire | undefined): boolean {
  return !!questionnaire && findChiefComplaintHpiMarker(questionnaire.item) !== undefined;
}

// ── templateItem → chip groups ──────────────────────────────────────────────────────────────

export interface ComplaintHpiOption {
  /** `templateItemObservation.itemId` — what the saved answer keys on. */
  itemId: string;
  /** Chip label — `templateCode.display`. */
  label: string;
  /** Wire `type` — 'normal' | 'posneg' | 'lrb' | 'image'. Carried onto the saved entry verbatim. */
  type: string;
  observation: TemplateItemObservation;
}

export interface ComplaintHpiGroup {
  /** `templateItem.templateId` — what the saved answer's `templateId` keys on. */
  templateId: string;
  /** `templateItem.itemTitle` — the group heading, and the saved answer's `category`. */
  title: string;
  /** Flutter NormalTag: one selected option at a time when the group is SingleChoice. */
  single: boolean;
  options: ComplaintHpiOption[];
}

function byDisplayOrder<T extends { displayOrder?: number }>(a: T, b: T): number {
  return (a.displayOrder ?? Number.MAX_SAFE_INTEGER) - (b.displayOrder ?? Number.MAX_SAFE_INTEGER);
}

/**
 * The complaint's server-resolved `templateItem[]` as render-ready chip groups: non-deleted
 * groups sorted by `displayOrder`, each with its non-deleted options sorted the same way (the
 * wire returns observations UNSORTED — captured 2026-09-11). A group with no renderable option
 * is dropped (nothing to select).
 *
 * `single` mirrors Flutter `TemplateItem.isSingleSelection`
 * (encounter_template_model.dart:252-253: `selection == "SingleChoice"`) — anything else,
 * including the wire's `MultiChoice` and a null, is multi-select.
 */
export function templateItemsToGroups(templateItem: ReadonlyArray<TemplateItem> | null | undefined): ComplaintHpiGroup[] {
  const groups: ComplaintHpiGroup[] = [];
  for (const ti of [...(templateItem ?? [])].sort(byDisplayOrder)) {
    if (ti.isDeleted || !ti.templateId) continue;
    const options: ComplaintHpiOption[] = [];
    for (const obs of [...(ti.templateItemObservation ?? [])].sort(byDisplayOrder)) {
      if (obs.isDeleted || !obs.itemId) continue;
      const label = obs.templateCode?.display?.trim();
      if (!label) continue;
      options.push({ itemId: obs.itemId, label, type: obs.type ?? 'normal', observation: obs });
    }
    if (options.length === 0) continue;
    groups.push({
      templateId: ti.templateId,
      title: ti.itemTitle?.trim() || ti.abbreviationTitle?.trim() || '',
      single: ti.selectionType === 'SingleChoice',
      options,
    });
  }
  return groups;
}

// ── Selection state ⇄ patientHpi ────────────────────────────────────────────────────────────

/** Composite key of one saved answer — the (group, option) pair. */
export function hpiKey(templateId: string | null | undefined, itemId: string | null | undefined): string {
  return `${templateId ?? ''}::${itemId ?? ''}`;
}

/** A saved entry that counts as SELECTED on load: an active, non-deleted 'yes' answer (the
 *  captured read shape — server fills `isActive: true` / `isDeleted: false`; a write echo has
 *  them null, which also passes). Comment-only rows (`status` null) are not selections. */
export function isSelectedHpiEntry(e: PatientHpiEntry): boolean {
  return e.isActive !== false && e.isDeleted !== true && e.status === 'yes' && !!e.itemId;
}

/** The selected (templateId, itemId) keys of a complaint's saved answers. */
export function selectedHpiKeys(entries: ReadonlyArray<PatientHpiEntry> | null | undefined): Set<string> {
  const out = new Set<string>();
  for (const e of entries ?? []) if (isSelectedHpiEntry(e)) out.add(hpiKey(e.templateId, e.itemId));
  return out;
}

export interface HpiSaveContext {
  encounterId: string;
  patientId: string;
}

/**
 * A NEW saved answer for one selected option — the exact wire entry Flutter posts.
 *
 * Source of the field set: `PatientClinicalCategoryData.toTemplate()`
 * (generated_notes_data.dart:548-572) builds `EncounterTemplateCategoryModel(identifier,
 * templateId, itemId, templateCode, status, subject, category, type, encounter, displayValue)`;
 * `toMap()` (encounter_template_category_model.dart:128-152) then serialises EVERY field, the
 * unset ones as null and `templateQuantity` as an all-null object. Values, per that source and
 * the captured dev POST (2026-09-11):
 *   identifier   null on a new row (toTemplate: `itemId != identifier ? identifier : null` —
 *                the wire's observations have itemId === identifier, so null)
 *   templateId   templateItem.templateId (the group)
 *   itemId       observation.itemId (the option)
 *   templateCode observation.templateCode verbatim
 *   status       'yes' (PatientClinicalItemStatus.yes.toData() — a tapped normal tag)
 *   category     templateItem.itemTitle
 *   displayValue '' (the `yes`-status branch of toTemplate yields '' for HPI)
 *   type         observation.type ('normal' — itemType().name)
 *   encounter / subject  the encounter and patient ids
 *   comment · note · notePlainText · effectiveDateTime · isActive · isDeleted  null
 */
export function newPatientHpiEntry(
  group: ComplaintHpiGroup,
  option: ComplaintHpiOption,
  ctx: HpiSaveContext,
): PatientHpiEntry {
  const code = option.observation.templateCode;
  return {
    identifier: null,
    templateId: group.templateId,
    itemId: option.itemId,
    templateCode: {
      system: code?.system ?? '',
      code: code?.code ?? '',
      display: code?.display ?? option.label,
    },
    status: 'yes',
    category: group.title,
    displayValue: '',
    comment: null,
    note: null,
    notePlainText: null,
    type: option.type,
    encounter: ctx.encounterId,
    subject: ctx.patientId,
    effectiveDateTime: null,
    templateQuantity: { value: null, unit: null, system: null, code: null },
    isActive: null,
    isDeleted: null,
  };
}

/**
 * The complaint's next `patientHpi[]` after tapping one chip.
 *
 * - already selected → the entry is DROPPED (whole-list replace; Flutter posts no tombstones);
 * - not selected, multi group → APPENDED with `identifier: null`;
 * - not selected, single group → every other selection IN THAT GROUP is dropped first (Flutter
 *   `onDataChange`, encounter_component_category_base_cell.dart:389: a single-selection group
 *   starts its new list empty), then appended.
 * Existing entries — other groups, comment rows, the untouched selections — pass through
 * VERBATIM (identifier and server columns kept), so a re-save echoes what the server holds.
 * Duplicates by (templateId, itemId) collapse to one (the Flutter app posted an option twice
 * and the server stored both — captured 2026-09-11; not replicated).
 */
export function toggleHpiSelection(
  entries: ReadonlyArray<PatientHpiEntry> | null | undefined,
  group: ComplaintHpiGroup,
  option: ComplaintHpiOption,
  ctx: HpiSaveContext,
): PatientHpiEntry[] {
  const target = hpiKey(group.templateId, option.itemId);
  const seen = new Set<string>();
  const kept: PatientHpiEntry[] = [];
  let wasSelected = false;
  for (const e of entries ?? []) {
    if (isSelectedHpiEntry(e)) {
      const k = hpiKey(e.templateId, e.itemId);
      if (k === target) {
        wasSelected = true;
        continue; // deselect — drop every copy
      }
      if (group.single && e.templateId === group.templateId) continue; // single: replace in-group
      if (seen.has(k)) continue; // de-dupe
      seen.add(k);
    }
    kept.push(e);
  }
  if (wasSelected) return kept;
  return [...kept, newPatientHpiEntry(group, option, ctx)];
}

/** Every selected option in ONE group dropped, in a single pass.
 *
 *  This exists because "Clear" cannot be expressed as N calls to `toggleHpiSelection` from a React
 *  host (Ahmed 2026-09-13: "it unselects the last option and keeps other options selected… if they
 *  were 3, it removes the last one and keeps two selected"). The host's toggle handler computes
 *  from the `ccDocument` it captured at render; a synchronous loop of N toggles never re-renders
 *  between calls, so all N start from the SAME document, each yields "the document minus one
 *  option", and the last write wins. Exactly one option disappears however many were selected.
 *
 *  Folding the pure `toggleHpiSelection` would also be correct, but this is one pass, one state
 *  update and ONE network write instead of N — and it cannot be re-broken by a caller that forgets
 *  to thread the accumulator.
 *
 *  Non-selected entries are preserved verbatim (they carry history the wire round-trips), and only
 *  entries keyed to THIS group's templateId + option ids are dropped, so sibling groups and other
 *  complaints are untouched. */
export function clearHpiGroup(
  entries: ReadonlyArray<PatientHpiEntry> | null | undefined,
  group: ComplaintHpiGroup,
): PatientHpiEntry[] {
  const targets = new Set(group.options.map((o) => hpiKey(group.templateId, o.itemId)));
  return (entries ?? []).filter(
    (e) => !(isSelectedHpiEntry(e) && targets.has(hpiKey(e.templateId, e.itemId))),
  );
}

/** Which complaint entry a chip belongs to — matched the way the write keys complaints: by
 *  coding display (see `buildPatientComplaint`). */
export function findComplaintByDisplay(
  model: PatientComplaintModel | undefined,
  display: string,
): ComplaintEntry | undefined {
  return model?.complaints.find((c) => isComplaintEntry(c) && c.coding?.display?.trim() === display);
}

/** The document with ONE complaint's `patientHpi` replaced — everything else untouched, so the
 *  result can be handed to `buildPatientComplaint` as `existing` and round-trip as usual. */
export function applyComplaintHpi(
  model: PatientComplaintModel,
  display: string,
  patientHpi: PatientHpiEntry[],
): PatientComplaintModel {
  return {
    ...model,
    complaints: model.complaints.map((c) =>
      isComplaintEntry(c) && c.coding?.display?.trim() === display ? { ...c, patientHpi } : c,
    ),
  };
}

// ── Generated note ──────────────────────────────────────────────────────────────────────────

/** Card / note heading — Flutter `"${title} (${complaintCategory})"`, parens omitted when the
 *  category is blank (chief_complaint_note_component_widget.dart.dart:140). */
export function complaintHeading(entry: ComplaintEntry): string {
  const display = entry.coding?.display?.trim() ?? '';
  const category = entry.complaintCategory?.trim();
  return category ? `${display} (${category})` : display;
}

// ── The DS boundary: complaint DTO -> ChiefComplaintHpiBlock props ───────────────────────────

/**
 * Map the complaint document to the PRESENTATION shape the Signal DS block takes.
 *
 * THIS IS THE DTO BOUNDARY, and it exists as one exported function rather than inline in the page
 * so the page and its tests cannot drift into two different mappings of the same document — the
 * defect class that keeps showing up here (two owners, one datum).
 *
 * WHY THE DS DOES NOT TAKE `ComplaintEntry[]`. That is the raw `GetPatientComplaint` row: nullable
 * throughout, and carrying a `templateItem` the write handler strips on POST. `hpiKey` is the
 * identity of an answer in THAT document. A design system that learned either would be coupled to
 * the Scrips wire, so the mapping stops here.
 *
 * The ids are chosen so the trip BACK is exact rather than a re-derivation:
 *   complaint.id = the coding display  (what every write keys on — `findComplaintByDisplay`)
 *   category.id  = `templateItem.templateId`
 *   option.id    = `templateItemObservation.itemId`
 * `findGroup` returns the ORIGINAL `ComplaintHpiGroup`, so a callback hands the existing handlers
 * the very object they already expect instead of a rebuilt lookalike.
 */
export interface ChiefComplaintHpiView {
  complaints: ChiefComplaintHpiComplaint[];
  findGroup: (complaintId: string, categoryId: string) => ComplaintHpiGroup | undefined;
}

/**
 * PROD-3042 (D-72): a category as the PA hands it to the DS — the DS shape plus `single`, the
 * group's `SingleChoice` selection type (`ComplaintHpiGroup.single`). The DS chip block ignores the
 * extra field (switch OFF draws exactly as before); the frame v2 attribute line reads it to draw a
 * single- or multi-select pick list.
 */
export type PaChiefComplaintHpiCategory = ChiefComplaintHpiCategory & {
  single?: boolean;
  /** The group's `templateItem.genderCategory` as the content carries it (`HpitemplateItems.GenderCategory`:
   *  'All' · 'Male' · 'Female'). Frame v2 reads it to leave out a group the content marks for the other sex. */
  genderCategory?: string;
};

/** The content's own sex mark on a group (`GenderCategory`), against the patient's registration sex ('M' / 'F').
 *  'Male' fits only M, 'Female' only F; anything else ('All', empty) fits everyone. No sex on record (not M or F)
 *  → every group fits: nothing is hidden for a fact the record does not hold. Never reads an option's words. */
export function categoryFitsSex(category: ChiefComplaintHpiCategory, sex: string | null | undefined): boolean {
  if (sex !== 'M' && sex !== 'F') return true;
  const mark = ((category as PaChiefComplaintHpiCategory).genderCategory ?? '').trim().toLowerCase();
  if (mark === 'male') return sex === 'M';
  if (mark === 'female') return sex === 'F';
  return true;
}

/** Does this category allow one value only? Absent → several (the wire's MultiChoice or null). */
export function isSingleChoiceCategory(category: ChiefComplaintHpiCategory): boolean {
  return (category as PaChiefComplaintHpiCategory).single === true;
}

/** Today's words for the HPI block's picker field (the DS default too). */
export const CC_PICKER_PLACEHOLDER = 'Enter Chief Complaint';
/** Frame v2, once one complaint is recorded: the field adds ANOTHER, so it says so. */
export const CC_PICKER_PLACEHOLDER_ANOTHER = 'Add another complaint';

/**
 * The picker placeholder for the HPI chief-complaint block.
 *
 * The DS draws the picker ABOVE the complaint cards, always empty, and its placeholder was always
 * "Enter Chief Complaint". With a complaint charted (its card and symptom chips right below), the
 * empty field still asked for a chief complaint, so a charted complaint read as unrecorded (dev test
 * walk, 1 Oct, beside an ambient record that said "written to chart"). The data agreed; the words
 * did not. Frame v2 only: OFF keeps today's placeholder for every count.
 */
export function chiefComplaintPickerPlaceholder(recordedCount: number, frameV2: boolean): string {
  return frameV2 && recordedCount > 0 ? CC_PICKER_PLACEHOLDER_ANOTHER : CC_PICKER_PLACEHOLDER;
}

/**
 * PROD-2947 · frame v2 (with `mappedOnly`, Scrips.Patient #1040): the groups of SAVED answers the complaint's
 * catalogue no longer lists — an answer recorded from the whole catalogue before the complaint was mapped, or before
 * the note asked for mapped groups only. Each keeps its saved title and only its chosen options, so a recorded answer
 * is never hidden and can still be un-picked; nothing new can be picked from it. In the order first saved.
 */
function savedOnlyGroups(entries: ReadonlyArray<PatientHpiEntry> | null | undefined, listed: ReadonlySet<string>): ComplaintHpiGroup[] {
  const byGroup = new Map<string, ComplaintHpiGroup>();
  for (const e of entries ?? []) {
    if (!isSelectedHpiEntry(e) || !e.templateId || listed.has(e.templateId)) continue;
    const label = e.templateCode?.display?.trim();
    if (!label || !e.itemId) continue;
    const group = byGroup.get(e.templateId) ?? { templateId: e.templateId, title: e.category?.trim() || '', single: false, options: [] };
    if (!group.options.some((o) => o.itemId === e.itemId)) {
      group.options.push({
        itemId: e.itemId,
        label,
        type: e.type ?? 'normal',
        observation: { itemId: e.itemId, templateCode: e.templateCode, type: e.type ?? 'normal' } as TemplateItemObservation,
      });
    }
    byGroup.set(e.templateId, group);
  }
  return [...byGroup.values()];
}

export function toChiefComplaintHpiView(
  entries: ReadonlyArray<ComplaintEntry> | null | undefined,
  opts: { keepSavedGroups?: boolean } = {},
): ChiefComplaintHpiView {
  // The same two filters the PA block applied before it graduated: quick-capture complaints only
  // (GlobalNotes rows are not complaints), and a complaint with no display has no identity — every
  // write keys on that string, so one without it could never be saved back.
  const usable = (entries ?? []).filter(
    (c) => isComplaintEntry(c) && !!c.coding?.display?.trim(),
  );
  const groupsByComplaint = new Map<string, ComplaintHpiGroup[]>();

  const complaints = usable.map((entry) => {
    const display = entry.coding?.display?.trim() ?? '';
    const listedGroups = templateItemsToGroups(entry.templateItem);
    const groups = opts.keepSavedGroups
      ? [...listedGroups, ...savedOnlyGroups(entry.patientHpi, new Set(listedGroups.map((g) => g.templateId)))]
      : listedGroups;
    const selected = selectedHpiKeys(entry.patientHpi);
    // PROD-3042: the content's own sex mark per group, keyed by the group's templateId.
    const genderOf = new Map((entry.templateItem ?? []).map((ti) => [ti.templateId ?? '', ti.genderCategory ?? undefined]));
    groupsByComplaint.set(display, groups);
    return {
      id: display,
      display,
      category: entry.complaintCategory?.trim() || undefined,
      // The canonical "{display} ({category})" form stays the ACCESSIBLE name, so assistive tech
      // and the generated note keep reading one clinical heading even though the reference splits
      // it across two visual lines.
      heading: complaintHeading(entry),
      categories: groups.map((g): PaChiefComplaintHpiCategory => ({
        id: g.templateId,
        title: g.title,
        single: g.single,
        ...(genderOf.get(g.templateId) ? { genderCategory: genderOf.get(g.templateId) as string } : {}),
        options: g.options.map((o) => ({
          id: o.itemId,
          label: o.label,
          selected: selected.has(hpiKey(g.templateId, o.itemId)),
        })),
      })),
    };
  });

  return {
    complaints,
    findGroup: (complaintId, categoryId) =>
      groupsByComplaint.get(complaintId)?.find((g) => g.templateId === categoryId),
  };
}


/**
 * The note's HPI rows when the block is active: one category per complaint, headed
 * `"{display} ({category})"`, its lines one per attribute GROUP — `Onset: 1 day ago, Sudden
 * onset` — built from the saved `patientHpi` (label = `templateCode.display`, group =
 * `category`), in the complaint's `templateItem` order where it is known. A complaint with no
 * selections contributes a heading with no lines, so the note still lists it (Flutter renders
 * every complaint's title, then `infoWidget` only when it has items).
 */
export function complaintHpiNoteCategories(
  complaints: ReadonlyArray<ComplaintEntry> | null | undefined,
): { title: string; lines: string[] }[] {
  const out: { title: string; lines: string[] }[] = [];
  const seen = new Set<string>();
  for (const c of complaints ?? []) {
    if (!isComplaintEntry(c)) continue;
    const title = complaintHeading(c);
    if (!title || seen.has(title)) continue;
    seen.add(title);
    // Group order: the template's, then any saved group the template no longer lists.
    const order = new Map<string, number>();
    templateItemsToGroups(c.templateItem).forEach((g, i) => order.set(g.templateId, i));
    const byGroup = new Map<string, { title: string; order: number; labels: string[] }>();
    const dedupe = new Set<string>();
    for (const e of c.patientHpi ?? []) {
      if (!isSelectedHpiEntry(e)) continue;
      const k = hpiKey(e.templateId, e.itemId);
      if (dedupe.has(k)) continue;
      dedupe.add(k);
      const label = e.templateCode?.display?.trim();
      if (!label) continue;
      const gid = e.templateId ?? '';
      const g = byGroup.get(gid) ?? {
        title: e.category?.trim() || '',
        order: order.get(gid) ?? Number.MAX_SAFE_INTEGER,
        labels: [],
      };
      g.labels.push(label);
      byGroup.set(gid, g);
    }
    const lines = [...byGroup.values()]
      .sort((a, b) => a.order - b.order)
      // Flutter joins the selected labels with ", " (joinListWithAnd, generated_notes_data.dart).
      .map((g) => (g.title ? `${g.title}: ${g.labels.join(', ')}` : g.labels.join(', ')));
    out.push({ title, lines });
  }
  return out;
}

/**
 * Splice the per-complaint rows into the step-grouped note's HPI section (the one
 * `groupNoteByStep` headed "Chief Complaint"). The complaint rows lead; any answered
 * questionnaire groups the template still authors beside the block follow. Only called when the
 * block is active — a template without the marker never reaches this.
 */
export function withChiefComplaintHpiNote(
  sections: NoteSection[],
  categories: { title: string; lines: string[] }[],
): NoteSection[] {
  return sections.map((s) => {
    if (s.step !== 'hpi') return s;
    const merged = [...categories, ...(s.categories ?? [])];
    return { ...s, categories: merged, lines: merged.flatMap((c) => c.lines) };
  });
}
